import React from 'react';
import { utcDate } from '../../utils/dates';

// The approval chain as a stepper (approved mockup Approvals): done ✓ · current (numbered, ringed) · upcoming ·
// skipped (dashed "–", with the reason) · sent back. chain = GET /api/jobs/{id}/chain.
const DOT = {
  done: 'bg-[#DCFCE7] text-[#166534]', current: 'bg-[#0066CC] text-white shadow-[0_0_0_4px_#DCEBFA]',
  upcoming: 'bg-[#F1F5F9] text-[#64748B]', skipped: 'bg-white text-[#94A3B8] border-[1.5px] border-dashed border-[#CBD5E1]',
  sent_back: 'bg-[#FEE2E2] text-[#991B1B]',
};

export function when(iso) {
  const d = utcDate(iso);
  return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
}

function line(step, me, startedAt) {
  const p = step.people || [];
  const decided = p.find((x) => x.decided_by);
  const names = [...new Set(p.flatMap((x) => x.names || []))];
  if (step.type === 'start') return { title: 'Hiring manager submitted', sub: when(step.at || startedAt) };
  if (step.status === 'skipped') return { title: step.label, sub: `Skipped — ${(step.reason || 'not required for this job').replace(/^The /, 'the ')}` };
  if (step.status === 'sent_back') return { title: `${step.label} — sent back`, sub: [decided && decided.decided_by, decided && when(decided.decided_at), decided && decided.comment && `“${decided.comment}”`].filter(Boolean).join(' · ') };
  if (step.type === 'recruiting') return { title: 'Recruiting', sub: step.status === 'done' ? 'Assigned recruiters start sourcing' : 'After assignment' };
  if (step.type === 'assign') return { title: `Assign${names.length ? ` — ${names.join(', ')}` : ''}`, sub: step.label };
  if (step.type === 'notify') return { title: `${step.label}`, sub: step.status === 'done' ? `Notified${step.at ? ` · ${when(step.at)}` : ''} — no action needed` : 'FYI — no action needed' };
  if (step.status === 'done') {
    const by = p.filter((x) => x.decided_by).map((x) => `${(x.decided_by || '').split(' ')[0]}${x.on_behalf_of ? ` for ${x.on_behalf_of.split(' ')[0]}` : ''}`);
    return { title: `${step.label} approved`, sub: [by.join(', '), decided && when(decided.decided_at)].filter(Boolean).join(' · ') };
  }
  if (step.status === 'current') {
    const mine = me && names.includes(me);
    const pending = p.find((x) => x.status === 'pending');
    const days = pending && pending.waiting_days != null ? `Waiting ${pending.waiting_days < 1 ? 'today' : `${Math.round(pending.waiting_days)} day${Math.round(pending.waiting_days) === 1 ? '' : 's'}`}` : 'Waiting';
    return { title: `${step.label} — ${mine ? 'you' : (names.length > 2 ? `${names.length} people` : names.map((n) => n.split(' ')[0]).join(', ') || 'company admins')}`,
      sub: [days, pending && pending.fallback_reason ? `no approver found (${pending.fallback_reason}) — company admins` : null].filter(Boolean).join(' · '), bold: true };
  }
  return { title: step.label, sub: names.length ? names.join(', ') : 'Upcoming' };
}

export default function ChainStepper({ chain, me }) {
  if (!chain || !chain.steps || !chain.steps.length) return <p className="text-sm text-[#64748B]">No approval chain for this job.</p>;
  let n = 0;
  return (
    <ol className="list-none p-0 m-0" data-testid="chain">
      {chain.steps.map((s, i) => {
        n += 1;
        const l = line(s, me, chain.started_at);
        const status = s.status === 'done' && s.type === 'start' ? 'done' : s.status;
        return (
          <li key={s.key} className="flex gap-3 relative pb-4" data-testid={`chain-${s.key}`} data-status={status}>
            {i < chain.steps.length - 1 && <span aria-hidden="true" className="absolute left-[13px] top-7 bottom-0 w-0.5 bg-[#E6E9F0]" />}
            <span className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-bold ${DOT[status] || DOT.upcoming}`} aria-hidden="true">
              {status === 'done' ? '✓' : status === 'skipped' ? '–' : status === 'sent_back' ? '!' : n}</span>
            <div className="min-w-0">
              <p className={`text-[13px] ${l.bold ? 'font-bold' : 'font-semibold'} ${status === 'skipped' ? 'text-[#64748B]' : 'text-[#0F172A]'}`}>{l.title}</p>
              {l.sub && <p className="text-xs text-[#64748B]">{l.sub}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
